import type {
  InviteTeamMemberBody,
  InviteTeamMemberResult,
  ReassignDealsResult,
  RoleValue,
  TeamMemberResource,
  TeamResource,
  UpdateTeamMemberBody,
} from '@majuscule/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { UserPlus } from 'lucide-react';
import { type JSX, useState } from 'react';
import { internalFetch, internalSend } from '../lib/api';
import { errorMessage } from '../lib/crm';
import { ME_KEY, ROLE_LABEL, useMe } from '../lib/me';
import { useToast } from '@/components/toast';
import { PageChrome } from '@/components/page-chrome';
import { Callout } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardFoot, CardHead } from '@/components/ui/card';
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHead } from '@/components/ui/dialog';
import { EmptyState, LoadingState } from '@/components/ui/empty';
import { Field } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/native-select';
import { PageHead } from '@/components/ui/page-header';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

const TEAM_KEY = ['team'];
const ROLES: readonly RoleValue[] = ['employee', 'admin'];

const ROLE_HELP: Record<RoleValue, string> = {
  admin: 'Voit et gère tout : clients facturés, marges, facturation, configuration, équipe.',
  employee:
    'Ne voit que le commercial (pipeline, entreprises, à faire) et seulement les affaires qu’il porte. Aucun montant de consommation, coût ni marge.',
};

function memberName(member: Pick<TeamMemberResource, 'displayName' | 'email'>): string {
  return member.displayName ?? member.email;
}

/** Page Équipe (lot 20) : inviter, changer le rôle, désactiver, transférer les affaires. */
export function TeamPage(): JSX.Element {
  const qc = useQueryClient();
  const toast = useToast();
  const me = useMe();
  const [inviteOpen, setInviteOpen] = useState(false);
  const [handOver, setHandOver] = useState<TeamMemberResource | null>(null);
  const [renaming, setRenaming] = useState<TeamMemberResource | null>(null);
  const [error, setError] = useState<string | null>(null);

  const team = useQuery({
    queryKey: TEAM_KEY,
    queryFn: () => internalFetch<TeamResource>('/internal/team'),
  });
  const refresh = (): void => {
    void qc.invalidateQueries({ queryKey: TEAM_KEY });
    void qc.invalidateQueries({ queryKey: ['crm'] });
  };

  const update = useMutation({
    mutationFn: (input: { id: string; body: UpdateTeamMemberBody }) =>
      internalSend<TeamMemberResource>('PATCH', `/internal/team/${input.id}`, input.body),
    onMutate: () => {
      setError(null);
    },
    onSuccess: (member) => {
      refresh();
      toast.notify(`${memberName(member)} : modifications enregistrées.`);
    },
    onError: (e) => {
      setError(errorMessage(e, 'Modification refusée.'));
    },
  });

  const members = team.data?.members ?? [];
  const active = members.filter((m) => m.disabledAt === null);

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Équipe' }]}
        actions={
          <Button
            type="button"
            onClick={() => {
              setInviteOpen(true);
            }}
          >
            <UserPlus aria-hidden="true" />
            Inviter
          </Button>
        }
      />
      <PageHead title="Équipe" subtitle="Accès à la console et droits de chacun" />

      {error !== null && <Callout variant="destructive">{error}</Callout>}

      <Card>
        <CardHead
          title="Membres"
          description="Un administrateur voit tout. Un commercial ne voit que ses affaires, sans marge ni configuration."
          className="pb-4"
        />
        {team.isLoading ? (
          <LoadingState className="border-t border-border" />
        ) : members.length === 0 ? (
          <EmptyState className="border-t border-border" title="Aucun membre" />
        ) : (
          <>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Membre</TableHead>
                  <TableHead>Rôle</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead kind="num">Affaires ouvertes</TableHead>
                  <TableHead kind="act">
                    <span className="sr-only">Actions</span>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {members.map((member) => {
                  const self = member.id === me.data?.id;
                  const disabled = member.disabledAt !== null;
                  return (
                    <TableRow key={member.id}>
                      <TableCell kind="name" className="min-w-[220px]">
                        <b className="block font-medium text-foreground">
                          {memberName(member)}
                          {self ? <span className="font-normal text-muted-foreground"> (vous)</span> : null}
                        </b>
                        {member.displayName !== null ? (
                          <span className="block text-caption text-muted-foreground">{member.email}</span>
                        ) : null}
                      </TableCell>
                      <TableCell>
                        <NativeSelect
                          aria-label={`Rôle de ${memberName(member)}`}
                          wrapperClassName="max-w-[180px]"
                          value={member.role}
                          disabled={self || update.isPending}
                          onChange={(event) => {
                            const role = ROLES.find((r) => r === event.target.value);
                            if (role !== undefined) {
                              update.mutate({ id: member.id, body: { role } });
                            }
                          }}
                        >
                          {ROLES.map((role) => (
                            <option key={role} value={role}>
                              {ROLE_LABEL[role]}
                            </option>
                          ))}
                        </NativeSelect>
                      </TableCell>
                      <TableCell>
                        {disabled ? <Badge variant="neutral">Désactivé</Badge> : <Badge variant="success">Actif</Badge>}
                      </TableCell>
                      <TableCell kind="num">{member.openDeals}</TableCell>
                      <TableCell kind="act">
                        <div className="flex justify-end gap-2">
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            onClick={() => {
                              setRenaming(member);
                            }}
                          >
                            Modifier le nom
                          </Button>
                          {member.openDeals > 0 ? (
                            <Button
                              type="button"
                              variant="outline"
                              size="xs"
                              onClick={() => {
                                setHandOver(member);
                              }}
                            >
                              Transférer les affaires
                            </Button>
                          ) : null}
                          {self ? null : (
                            <Button
                              type="button"
                              variant={disabled ? 'outline' : 'outline-destructive'}
                              size="xs"
                              disabled={update.isPending}
                              onClick={() => {
                                update.mutate({ id: member.id, body: { disabled: !disabled } });
                              }}
                            >
                              {disabled ? 'Réactiver' : 'Désactiver'}
                            </Button>
                          )}
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            <CardFoot
              start={`${String(active.length)} membre${active.length > 1 ? 's' : ''} actif${active.length > 1 ? 's' : ''}`}
            />
          </>
        )}
      </Card>

      <Card>
        <CardHead title="Ce que voit chaque rôle" className="pb-4" />
        <div className="grid gap-3 border-t border-border p-4 text-[13px] leading-[18px]">
          {ROLES.map((role) => (
            <p key={role} className="m-0">
              <b className="font-medium text-foreground">{ROLE_LABEL[role]}.</b>{' '}
              <span className="text-muted-foreground">{ROLE_HELP[role]}</span>
            </p>
          ))}
        </div>
      </Card>

      <InviteDialog
        open={inviteOpen}
        onClose={() => {
          setInviteOpen(false);
        }}
        onInvited={(result) => {
          refresh();
          toast.notify(
            result.invited
              ? `Invitation envoyée à ${result.member.email}.`
              : `${result.member.email} a déjà un compte : accès ouvert, sans e-mail.`,
          );
        }}
      />
      <RenameDialog
        member={renaming}
        onClose={() => {
          setRenaming(null);
        }}
        onRenamed={(member) => {
          refresh();
          void qc.invalidateQueries({ queryKey: ME_KEY });
          toast.notify(`${memberName(member)} : nom enregistré.`);
        }}
      />
      <HandOverDialog
        from={handOver}
        members={active}
        onClose={() => {
          setHandOver(null);
        }}
        onDone={(result, from) => {
          refresh();
          toast.notify(
            `${String(result.deals)} affaire${result.deals > 1 ? 's' : ''} de ${memberName(from)} transférée${result.deals > 1 ? 's' : ''}.`,
          );
        }}
      />
    </>
  );
}

function InviteDialog({
  open,
  onClose,
  onInvited,
}: {
  open: boolean;
  onClose: () => void;
  onInvited: (result: InviteTeamMemberResult) => void;
}): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {open ? <InviteForm onClose={onClose} onInvited={onInvited} /> : null}
    </Dialog>
  );
}

function InviteForm({
  onClose,
  onInvited,
}: {
  onClose: () => void;
  onInvited: (result: InviteTeamMemberResult) => void;
}): JSX.Element {
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [role, setRole] = useState<RoleValue>('employee');
  const invite = useMutation({
    mutationFn: () => {
      const body: InviteTeamMemberBody = {
        email: email.trim(),
        displayName: displayName.trim() === '' ? null : displayName.trim(),
        role,
      };
      return internalSend<InviteTeamMemberResult>('POST', '/internal/team', body);
    },
    onSuccess: (result) => {
      onInvited(result);
      onClose();
    },
  });

  return (
    <DialogContent>
      <DialogHead
        title="Inviter un membre"
        description="La personne reçoit un e-mail avec un lien pour choisir son mot de passe."
      />
      <form
        id="invite-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          invite.mutate();
        }}
      >
        <DialogBody>
          <Field label="E-mail *">
            <Input
              type="email"
              required
              maxLength={254}
              value={email}
              placeholder="prenom@exemple.fr"
              onChange={(event) => {
                setEmail(event.target.value);
              }}
            />
          </Field>
          <Field label="Prénom et nom">
            <Input
              maxLength={100}
              value={displayName}
              placeholder="Prénom Nom"
              onChange={(event) => {
                setDisplayName(event.target.value);
              }}
            />
          </Field>
          <Field label="Rôle">
            <NativeSelect
              value={role}
              onChange={(event) => {
                const found = ROLES.find((r) => r === event.target.value);
                if (found !== undefined) {
                  setRole(found);
                }
              }}
            >
              {ROLES.map((value) => (
                <option key={value} value={value}>
                  {ROLE_LABEL[value]}
                </option>
              ))}
            </NativeSelect>
          </Field>
          <p className="m-0 text-caption text-muted-foreground">{ROLE_HELP[role]}</p>
          {invite.isError ? (
            <Callout variant="destructive">{errorMessage(invite.error, 'Invitation refusée.')}</Callout>
          ) : null}
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="invite-form" size="lg" disabled={invite.isPending}>
          {invite.isPending ? 'Envoi…' : 'Envoyer l’invitation'}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function HandOverDialog({
  from,
  members,
  onClose,
  onDone,
}: {
  from: TeamMemberResource | null;
  members: readonly TeamMemberResource[];
  onClose: () => void;
  onDone: (result: ReassignDealsResult, from: TeamMemberResource) => void;
}): JSX.Element {
  return (
    <Dialog
      open={from !== null}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {from !== null ? (
        <HandOverForm
          from={from}
          members={members.filter((m) => m.id !== from.id)}
          onClose={onClose}
          onDone={onDone}
        />
      ) : null}
    </Dialog>
  );
}

function HandOverForm({
  from,
  members,
  onClose,
  onDone,
}: {
  from: TeamMemberResource;
  members: readonly TeamMemberResource[];
  onClose: () => void;
  onDone: (result: ReassignDealsResult, from: TeamMemberResource) => void;
}): JSX.Element {
  const [toId, setToId] = useState(members[0]?.id ?? '');
  const reassign = useMutation({
    mutationFn: () =>
      internalSend<ReassignDealsResult>('POST', `/internal/team/${from.id}/reassign`, { toId }),
    onSuccess: (result) => {
      onDone(result, from);
      onClose();
    },
  });
  return (
    <DialogContent>
      <DialogHead
        title="Transférer les affaires"
        description={`Les ${String(from.openDeals)} affaires ouvertes de ${memberName(from)}, et ses rappels hors affaire, passent au membre choisi. Les affaires gagnées ou perdues gardent leur porteur.`}
      />
      <DialogBody>
        <Field label="Nouveau porteur">
          <NativeSelect
            value={toId}
            onChange={(event) => {
              setToId(event.target.value);
            }}
          >
            {members.map((member) => (
              <option key={member.id} value={member.id}>
                {memberName(member)} ({ROLE_LABEL[member.role]})
              </option>
            ))}
          </NativeSelect>
        </Field>
        {reassign.isError ? (
          <Callout variant="destructive">{errorMessage(reassign.error, 'Transfert refusé.')}</Callout>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button
          type="button"
          size="lg"
          disabled={reassign.isPending || toId === ''}
          onClick={() => {
            reassign.mutate();
          }}
        >
          Transférer
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

function RenameDialog({
  member,
  onClose,
  onRenamed,
}: {
  member: TeamMemberResource | null;
  onClose: () => void;
  onRenamed: (member: TeamMemberResource) => void;
}): JSX.Element {
  return (
    <Dialog
      open={member !== null}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {member !== null ? <RenameForm member={member} onClose={onClose} onRenamed={onRenamed} /> : null}
    </Dialog>
  );
}

function RenameForm({
  member,
  onClose,
  onRenamed,
}: {
  member: TeamMemberResource;
  onClose: () => void;
  onRenamed: (member: TeamMemberResource) => void;
}): JSX.Element {
  const [displayName, setDisplayName] = useState(member.displayName ?? '');
  const rename = useMutation({
    mutationFn: () => {
      const body: UpdateTeamMemberBody = {
        displayName: displayName.trim() === '' ? null : displayName.trim(),
      };
      return internalSend<TeamMemberResource>('PATCH', `/internal/team/${member.id}`, body);
    },
    onSuccess: (updated) => {
      onRenamed(updated);
      onClose();
    },
  });
  return (
    <DialogContent>
      <DialogHead
        title="Modifier le nom"
        description={`Affiché dans la console à la place de l’e-mail (${member.email}) : barre latérale, porteur des affaires, équipe.`}
      />
      <form
        id="rename-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          rename.mutate();
        }}
      >
        <DialogBody>
          <Field label="Prénom et nom">
            <Input
              maxLength={100}
              value={displayName}
              placeholder="Prénom Nom"
              onChange={(event) => {
                setDisplayName(event.target.value);
              }}
            />
          </Field>
          {rename.isError ? (
            <Callout variant="destructive">{errorMessage(rename.error, 'Modification refusée.')}</Callout>
          ) : null}
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="rename-form" size="lg" disabled={rename.isPending}>
          Enregistrer
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}
